<script>
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher();
  
  export let properties = [];
  export let reservedKeys = [];
  export let keyPlaceholder = "Property name";
  export let valuePlaceholder = "Property value";
  export let title = "Custom Properties";
  export let description = "Add custom key-value properties";
  export let maxProperties = 20;
  
  // Internal working state to prevent binding issues
  let workingProperties = [];
  
  // Sync working properties with prop changes
  $: workingProperties = Array.isArray(properties) ? [...properties] : [];
  $: console.log('property editor working properties ', workingProperties);
  
  function addProperty() {
    if (workingProperties.length >= maxProperties) return;
    const prop_index = workingProperties.length;
    const newProperties = [...workingProperties, { key: "prop_key_" + prop_index, value: "prop_value_" + prop_index }];
    workingProperties = newProperties;
    dispatch('update', newProperties);
  }
  
  function removeProperty(index) {
    const newProperties = workingProperties.filter((_, i) => i !== index);
    workingProperties = newProperties;
    dispatch('update', newProperties);
  }
  
  function updateProperty(index, field, value) {
    const newProperties = [...workingProperties];
    newProperties[index] = { ...newProperties[index], [field]: value };
    workingProperties = newProperties;
    dispatch('update', newProperties);
  }
  
  function isReservedKey(key) {
    return reservedKeys.includes(key);
  }
  
  function isDuplicateKey(key, currentIndex) {
    return workingProperties.some((prop, index) => 
      index !== currentIndex && prop.key === key && key !== ''
    );
  }
  
  function getKeyWarning(key, index) {
    if (isReservedKey(key)) {
      return 'Reserved key - will be ignored';
    }
    if (isDuplicateKey(key, index)) {
      return 'Duplicate key';
    }
    return null;
  }
</script>

<div class="property-editor">
  <div class="property-header">
    <h6>{title}</h6>
    {#if workingProperties.length > 0}
      <span class="property-count-badge">
        {workingProperties.length} / {maxProperties}
      </span>
    {/if}
  </div>
  
  {#if workingProperties.length === 0}
    <div class="no-properties">
      <p class="no-properties-text">No custom properties defined</p>
      <button class="btn btn-secondary" on:click={addProperty}>
        Add First Property
      </button>
    </div>
  {:else}
    <div class="property-list">
      {#each workingProperties as prop, idx}
        {@const keyWarning = getKeyWarning(prop.key, idx)}
        <div class="property-item" class:has-warning={keyWarning}>
          <div class="property-key-wrapper">
            <input 
              type="text"
              value={prop.key || ''}
              placeholder={keyPlaceholder}
              on:input={(e) => updateProperty(idx, 'key', e.target.value)}
              class="property-key"
              class:reserved={isReservedKey(prop.key)}
              class:duplicate={isDuplicateKey(prop.key, idx)}
            />
            {#if keyWarning}
              <span class="key-warning" title={keyWarning}>⚠</span>
            {/if}
          </div>
          
          <span class="property-separator">:</span>
          
          <input 
            type="text"
            value={prop.value || ''}
            placeholder={valuePlaceholder}
            on:input={(e) => updateProperty(idx, 'value', e.target.value)}
            class="property-value"
          />
          
          <button 
            class="btn btn-small btn-danger"
            on:click={() => removeProperty(idx)}
            title="Remove property"
          >
            ×
          </button>
        </div>
        
        {#if keyWarning}
          <div class="property-warning">
            {keyWarning}
          </div>
        {/if}
      {/each}
    </div>
    
    <div class="property-footer">
      {#if workingProperties.length < maxProperties}
        <button class="btn btn-secondary" on:click={addProperty}>
          Add Property
        </button>
      {/if}
    </div>
  {/if}
  
  <p class="property-help">{description}</p>
  
  {#if reservedKeys.length > 0}
    <div class="reserved-keys-info">
      <p class="reserved-keys-title">Reserved keys:</p>
      <div class="reserved-keys-list">
        {#each reservedKeys as key}
          <code class="reserved-key">{key}</code>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .property-editor {
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.7);
  }
  
  .property-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    h6 {
      margin: 0;
      font-size: 0.8rem;
      font-weight: bold;
      color: #374151;
    }
  }
  
  .property-count-badge {
    background: #6b7280;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .no-properties {
    text-align: center;
    padding: 20px;
    border: 2px dashed #d1d5db;
    border-radius: 6px;
    background: #f9fafb;
    
    .no-properties-text {
      margin-bottom: 12px;
      color: #6b7280;
      font-style: italic;
      font-size: 0.8rem;
    }
  }
  
  .property-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .property-item {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px;
    border-radius: 4px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f3f4f6;
    }
    
    &.has-warning {
      border-color: #f59e0b;
      background: #fef3c7;
    }
  }
  
  .property-key-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    
    .key-warning {
      position: absolute;
      right: 6px;
      color: #f59e0b;
      font-size: 12px;
      pointer-events: none;
    }
  }
  
  .property-key {
    width: 100%;
    padding: 6px 24px 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    font-size: 0.75rem;
    font-weight: 500;
    font-family: monospace;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px #3b82f6;
    }
    
    &.reserved {
      border-color: #f59e0b;
      background: #fef3c7;
    }
    
    &.duplicate {
      border-color: #ef4444;
      background: #fee2e2;
    }
    
    &::placeholder {
      color: #9ca3af;
      font-weight: normal;
    }
  }
  
  .property-separator {
    color: #6b7280;
    font-weight: bold;
    font-size: 14px;
    flex: 0 0 auto;
  }
  
  .property-value {
    flex: 2;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    font-size: 0.75rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px #3b82f6;
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .property-warning {
    font-size: 11px;
    color: #f59e0b;
    font-style: italic;
    margin-left: 12px;
    margin-top: -4px;
    margin-bottom: 4px;
  }
  
  .property-footer {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
  }
  
  .property-help {
    font-size: 11px;
    color: #6b7280;
    margin: 10px 0 0 0;
    font-style: italic;
  }
  
  .reserved-keys-info {
    margin-top: 10px;
    padding: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    
    .reserved-keys-title {
      font-size: 11px;
      color: #374151;
      margin-bottom: 5px;
      font-weight: 500;
    }
    
    .reserved-keys-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      
      .reserved-key {
        background: #e2e8f0;
        padding: 1px 4px;
        border-radius: 2px;
        font-family: monospace;
        font-size: 10px;
        color: #475569;
      }
    }
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
    
    &.btn-small {
      padding: 4px 6px;
      font-size: 10px;
    }
  }
</style>